// The emoji picker's data, written by scripts/emojis.js and fetched apart from the app's code. The grid, the
// emojis by group, is small: the calendar fetches it when the browser is idle after loading, so the picker
// is ready at once. The names and search words, most of the data, are fetched when the picker first opens.

const once = (load) => {
    let request = null;
    return () => {
        if (request == null) {
            request = load().catch((err) => {
                request = null;
                throw err;
            });
        }
        return request;
    };
};

// Groups as { key, emojis: [[emoji, Emoji version, skin tones?], ...] }.
export const loadEmojiGrid = once(() => import('./emoji-grid.json').then((module) => module.default.groups));

// { emoji: [Russian name, search words] }.
export const loadEmojiWords = once(() => import('./emoji-words.json').then((module) => module.default.names));

// Emojis newer than the system's emoji font show as empty boxes, so the picker stops at the first Emoji version
// the browser can't draw in colour. Each version is tested with an emoji that is coloured by default: symbols
// with U+FE0F can be grey when supported, and ZWJ sequences and flags still draw as several coloured emojis
// when not. Versions without such an emoji aren't tested.
let drawableBelow = null;
export const firstUndrawableVersion = (groups) => {
    if (drawableBelow != null) return drawableBelow;
    drawableBelow = Infinity;
    const ctx = /jsdom/.test(navigator.userAgent) ? null : document.createElement('canvas').getContext('2d', { willReadFrequently: true });
    if (!ctx) return drawableBelow;

    const testEmoji = new Map();
    groups.filter(group => group.key !== 'flags').forEach(group => group.emojis.forEach(([emoji, version]) => {
        if (!testEmoji.has(version) && !/[‍️]/.test(emoji)) testEmoji.set(version, emoji);
    }));
    const isColoured = (emoji) => {
        ctx.clearRect(0, 0, 32, 32);
        ctx.font = '24px sans-serif';
        ctx.textBaseline = 'top';
        ctx.fillStyle = '#000';
        ctx.fillText(emoji, 0, 0);
        const pixels = ctx.getImageData(0, 0, 32, 32).data;
        for (let i = 0; i < pixels.length; i += 4) {
            if (pixels[i + 3] > 0 && (pixels[i] !== pixels[i + 1] || pixels[i + 1] !== pixels[i + 2])) return true;
        }
        return false;
    };
    const versions = [...testEmoji.keys()].sort((a, b) => a - b);
    // A browser without any colour emoji font keeps the whole list.
    if (!isColoured(testEmoji.get(versions[0]))) return drawableBelow;
    drawableBelow = versions.find(version => !isColoured(testEmoji.get(version))) ?? Infinity;
    return drawableBelow;
};

// Fetches the grid and runs the test above while the browser has nothing else to do.
export const preloadEmojis = () => {
    const whenIdle = window.requestIdleCallback ?? ((callback) => setTimeout(callback, 200));
    whenIdle(() => loadEmojiGrid().then((groups) => whenIdle(() => firstUndrawableVersion(groups))).catch(() => {}));
};
